// @flow
import * as React from 'react';
import kebabCase from 'lodash/fp/kebabCase';

import { memoize } from 'modules/memoize';
import {
  translateWithTagReplacements,
  type Replacements,
  type Translations,
} from 'modules/l10n';
import {
  factory,
  withFallbackFactory,
  L10nContext,
  type TranslateWithFallback,
  type AddedProps as L10nAddedProps,
} from 'ui/l10n-context';
import type { RenderFunctionMap } from 'modules/xml';

export { TranslateType as Translate } from 'modules/l10n';

export type LocalizedTranslateWithTranslationsAndTagReplacements = (
  key: string,
  replacements: Replacements,
  tagReplacements: RenderFunctionMap,
) => React.Node;

export type AddedProps = $ReadOnly<{|
  translateWithFallback: TranslateWithFallback,
  translateWithTagReplacements: LocalizedTranslateWithTranslationsAndTagReplacements,
  ...L10nAddedProps,
  kebabCaseLocale: string,
|}>;

const translateWithTagReplacementsFactory = (memoize(
  (locale: string) =>
    (memoize(
      (translations: Translations) =>
        (
          key: string,
          replacements: Replacements,
          tagReplacements: RenderFunctionMap = { ...null },
        ) =>
          translateWithTagReplacements(
            key,
            replacements,
            tagReplacements,
            translations,
            locale,
          ),
      (translations) => JSON.stringify(translations),
    ): (Translations) => LocalizedTranslateWithTranslationsAndTagReplacements),
): (string) => (Translations) => LocalizedTranslateWithTranslationsAndTagReplacements);

const addedPropsFactory = (memoize(
  (locale: string, translations: Translations): AddedProps => ({
    locale,
    kebabCaseLocale: kebabCase(locale),
    translations,
    translate: factory(locale, translations),
    translateWithFallback: withFallbackFactory(translations),
    translateWithTagReplacements:
      translateWithTagReplacementsFactory(locale)(translations),
  }),
  (locale, translations) => JSON.stringify([locale, translations]),
): (string, Translations) => AddedProps);

export default (): AddedProps => {
  const { locale, translations } = React.useContext(L10nContext);
  return addedPropsFactory(locale, translations);
};
